import { useState, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Minus, CirclePlus } from "lucide-react"
import MediaUploader from "./media_uploader"
import TwitterProfileInfo from "./twitter_profile_info"
import VoiceNoteWidget from "@/components/audio/voice_note_widget"

const TwitterThreadEditor = ({ 
  posts, 
  onUpdatePost, 
  onAddPost, 
  onRemovePost, 
  joinedContent, 
  initialContent, 
  saveUrl, 
  onSaveStart, 
  onSaveEnd, 
  assets = [], 
  onMediaChange, 
  mediaSelection,
  displayName,
  handleLabel,
  avatarUrl,
  verified,
  disabled
}) => {
  const [saveTimeout, setSaveTimeout] = useState(null)
  const postsRef = useRef(posts)

  useEffect(() => {
    postsRef.current = posts
  }, [posts])

  const appendTranscript = (transcript) => {
    if (!transcript) return

    const currentContent = postsRef.current[0] || ""
    const separator = currentContent.trim() ? "\n\n" : ""
    onUpdatePost(0, `${currentContent}${separator}${transcript}`)
  }

  useEffect(() => {
    if (!saveUrl || !onSaveStart || !onSaveEnd || typeof joinedContent === 'undefined' || typeof initialContent === 'undefined') {
      if (saveTimeout) clearTimeout(saveTimeout);
      return;
    }

    if (saveTimeout) clearTimeout(saveTimeout)

    if (joinedContent === initialContent) {
      return
    }

    const timeout = setTimeout(() => {
      onSaveStart()

      const data = { post: { twitter_content: joinedContent } }
      const options = {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => { /* Parent handles state */ },
        onError: () => { /* Parent handles state */ },
        onFinish: () => onSaveEnd()
      }

      router.patch(saveUrl, data, options)

    }, 750)

    setSaveTimeout(timeout)

    return () => {
      if (timeout) clearTimeout(timeout)
    }

  }, [joinedContent, initialContent, saveUrl, onSaveStart, onSaveEnd])

  return (
    <div className="space-y-4 rounded-lg p-4">
      {/* First post with profile info */}
      <div className="space-y-2">
        <TwitterProfileInfo 
          displayName={displayName}
          handleLabel={handleLabel}
          avatarUrl={avatarUrl}
          verified={verified}
        />
        <Textarea
          value={posts[0] || ""}
          onChange={(e) => onUpdatePost(0, e.target.value)}
          placeholder={`What's happening?`}
          disabled={disabled}
          className="min-h-[100px] !border-0 !bg-transparent !ring-0 !ring-offset-0 focus-visible:!ring-0 resize-none"
        />
      </div>

      {/* Media preview - only show when there's a selection, centered */}
      {mediaSelection?.url && (
        <div className="flex justify-center">
          <MediaUploader
            assets={assets}
            onChange={onMediaChange}
            currentSelection={mediaSelection}
            disabled={disabled}
          />
        </div>
      )}

      {/* Additional thread posts - below media, each with profile info */}
      {posts.length > 1 && (
        <div className="space-y-4 border-t pt-4">
          {posts.slice(1).map((post, index) => (
            <div key={index + 1} className="space-y-2">
              <div className="flex items-center justify-between">
                <TwitterProfileInfo 
                  displayName={displayName}
                  handleLabel={handleLabel}
                  avatarUrl={avatarUrl}
                  verified={verified}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemovePost(index + 1)}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
              <Textarea
                value={post}
                onChange={(e) => onUpdatePost(index + 1, e.target.value)}
                placeholder={`Continue your thread...`}
                className="min-h-[100px] !border-0 !bg-transparent !ring-0 !ring-offset-0 focus-visible:!ring-0 resize-none"
              />
            </div>
          ))}
        </div>
      )}

      {/* Post Options - media upload button and add post side by side */}
      {!disabled && (
      <div className="flex min-h-9 items-center gap-1">
        <div className="flex h-7 items-center">
          {!mediaSelection?.url && (
            <MediaUploader
              assets={assets}
              onChange={onMediaChange}
              currentSelection={mediaSelection}
              disabled={disabled}
              compactTrigger
            />
          )}
          <button
            type="button"
            onClick={onAddPost}
            className="flex h-7 w-7 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
          >
            <CirclePlus className="h-4 w-4" />
          </button>
        </div>
        <VoiceNoteWidget
          variant="toolbar"
          createHighlight={false}
          onTranscript={appendTranscript}
        />
      </div>
      )}
    </div>
  )
}

export default TwitterThreadEditor
